import Quill from 'quill';
import { provideQuillConfig } from 'ngx-quill';

/* ---------------------------------------------------
 * FONT WHITELIST (TYPE-SAFE)
 * --------------------------------------------------- */
const FontClass = Quill.import('formats/font') as any;
FontClass.whitelist = [
  'sansserif',
  'inter',
  'roboto',
  'arial',
  'georgia',
  'times',
  'mono',
];
Quill.register('formats/font', FontClass, true);

/* ---------------------------------------------------
 * FONT SIZE WHITELIST (INLINE STYLE FOR EMAIL HTML)
 * --------------------------------------------------- */
const SizeStyle = Quill.import('attributors/style/size') as any;
SizeStyle.whitelist = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '32px',
  '48px',
  '64px',
  '102px'
];
Quill.register(SizeStyle, true);

/* ---------------------------------------------------
 * COLOR WHITELIST (TYPE-SAFE)
 * --------------------------------------------------- */
const ColorClass = Quill.import('formats/color') as any;
ColorClass.whitelist = [
  '#000000', // black
  '#374151', // gray-700
  '#6B7280', // gray-500
  '#9CA3AF', // gray-400

  '#2563EB', // blue-600
  '#1D4ED8', // blue-700
  '#1E40AF', // blue-800

  '#16A34A', // green-600
  '#15803D', // green-700

  '#DC2626', // red-600
  '#B91C1C', // red-700

  '#D97706', // amber-600
  '#92400E', // amber-800
];

Quill.register('formats/color', ColorClass, true);

/* ---------------------------------------------------
 * IMAGE UPLOAD HANDLER (NO `this`)
 * --------------------------------------------------- */
function imageUploadHandler(quill: Quill) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png,image/jpeg,image/webp';
  input.click();

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch('/api/uploads/editor-image', {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}`,
      },
    });

    const { url } = await res.json();

    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'image', url);
  };
}

/* ---------------------------------------------------
 * GLOBAL QUILL CONFIG
 * --------------------------------------------------- */
export const QUILL_GLOBAL_PROVIDER = provideQuillConfig({
  theme: 'snow',

  formats: [
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'size',
    'align',
    'link',
    'blockquote',
    'code-block',
    'color',
    'font',
    'image',
  ],

  modules: {
    toolbar: {
      container: [
        [{ font: ['sansserif','inter', 'roboto', 'arial', 'georgia', 'times', 'mono'] }],
        [{ size: SizeStyle.whitelist }],
        [{ color: ColorClass.whitelist }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image() {
          imageUploadHandler(this.quill);
        },
      },
    },

    clipboard: {
      matchVisual: false,
    },

    history: {
      delay: 1000,
      maxStack: 100,
      userOnly: true,
    },

    keyboard: {
      bindings: {
        tab: false,
      },
    },
  },

  sanitize: true,
});
